// components/schedule/AppointmentCard.js
import React from 'react';
import { Service } from '../../models/Service';

export function AppointmentCard({
  appointment,
  crewMember,
  service,
  slotsNeeded,
  onClick,
  className = ''
}) {
  const formatTime = (timeString) => {
    const [hours, minutes] = timeString.split(':');
    const date = new Date();
    date.setHours(parseInt(hours), parseInt(minutes));
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'ring-2 ring-green-400 ring-opacity-50';
      case 'cancelled': return 'opacity-50 ring-2 ring-red-400 ring-opacity-50';
      case 'completed': return 'ring-2 ring-gray-400 ring-opacity-50';
      case 'no-show': return 'ring-2 ring-orange-400 ring-opacity-50';
      default: return '';
    }
  };

  // Check if payment info should be hidden for this service
  const shouldShowPayment = !Service.getServicesWithoutPayment().includes(appointment.service_name);

  const tooltipText = [
    `Customer: ${appointment.customer_name}`,
    `Service: ${appointment.service_name}`,
    `Time: ${formatTime(appointment.time)} - ${appointment.getEndTime()}`,
    `Crew: ${appointment.crew_member_name || 'Unassigned'}`,
    `Status: ${appointment.status}`,
    shouldShowPayment ? `Payment: ${appointment.is_paid ? 'Paid' : 'Unpaid'}` : null,
    appointment.notes ? `Notes: ${appointment.notes}` : null
  ].filter(Boolean).join('\n');

  return (
    <div
      className={`absolute inset-0 p-1 rounded text-xs text-white cursor-pointer overflow-hidden transition-all hover:shadow-lg ${getStatusColor(appointment.status)} ${className}`}
      style={{
        backgroundColor: service?.color || '#F8BBD9',
        height: `${slotsNeeded * 100}%`
      }}
      onClick={onClick}
      title={tooltipText}
    >
      <div className="flex items-start justify-between h-full">
        <div className="flex-1 min-w-0">
          {/* Customer Name */}
          <div className="font-medium truncate text-shadow-sm">
            {appointment.customer_name}
          </div>

          {/* Service Name */}
          <div className="text-xs opacity-90 truncate">
            {appointment.service_name}
          </div>

          {/* Time Range */}
          <div className="text-xs opacity-75 truncate">
            {formatTime(appointment.time)} - {appointment.getEndTime()}
          </div>

          {/* Status Indicator for non-scheduled appointments */}
          {appointment.status !== 'scheduled' && (
            <div className="text-xs opacity-90 font-medium">
              {appointment.status.toUpperCase()}
            </div>
          )}
        </div>

        {/* Crew Member Avatar/Initials */}
        <div className="flex-shrink-0 flex flex-col items-center ml-1">
          <div
            className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold border border-white border-opacity-30"
            style={{
              backgroundColor: crewMember?.color || '#6B7280',  // CHANGE: use crew member color for avatar background
              color: 'white'  // Ensure text is white on colored background
            }}
            title={appointment.crew_member_name || 'Unassigned'}
          >
            {appointment.getCrewMemberInitials() || '?'}
          </div>
        </div>

        {/* Duration indicator for longer appointments */}
        {appointment.duration > 60 && (
          <div className="text-xs opacity-75 mt-1">
            {Math.round(appointment.duration / 60)}h
          </div>
        )}
      </div>

      {/* Bottom border for visual separation */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-black bg-opacity-10"></div>
    </div>
  );
}

// Utility component for empty time slots with crew member context
export function EmptyTimeSlot({
  date,
  time,
  selectedCrewMember,
  activeCrewMembers,
  onClick,
  className = ''
}) {
  const crewMemberName = selectedCrewMember === 'all'
    ? 'Any crew member'
    : activeCrewMembers.find(cm => cm.id === selectedCrewMember)?.name || 'Unknown';

  return (
    <div
      className={`absolute inset-0 hover:bg-pink-50 hover:border-pink-200 border border-transparent rounded cursor-pointer transition-colors touch-manipulation ${className}`}
      onClick={onClick}
      title={`Add appointment at ${time} for ${crewMemberName}`}
    >
      <div className="w-full h-full flex items-center justify-center">
        <div className="text-gray-400 text-sm md:text-xs opacity-0 hover:opacity-100 transition-opacity">
          +
        </div>
      </div>
    </div>
  );
}
